'use client';

import { useCallback, useLayoutEffect, useRef, useState } from 'react';

interface UseScrollTrackingOptions {
  /** Whether the device is touch-based (mobile) */
  isTouchDevice: boolean;
  /** Array of section IDs to track */
  sections: Array<{ id: string }>;
}

interface UseScrollTrackingReturn {
  /** ID of the currently active section (scroll-based) */
  activeSectionId: string | null;
  /** ID of the currently hovered section (pointer-based) */
  hoveredSectionId: string | null;
  /** Container ref for scroll tracking */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Map of section refs for intersection tracking */
  sectionRefs: React.MutableRefObject<Map<string, HTMLElement>>;
  /** Create a ref setter for a section */
  setSectionRef: (id: string) => (el: HTMLElement | null) => void;
  /** Set the hovered section (for pointer devices) */
  setHoveredSectionId: (id: string | null) => void;
  /** Get the current active section ID based on device type */
  currentActiveSectionId: string | null;
}

/**
 * Hook for tracking scroll position and determining the active section.
 *
 * On touch devices, tracks scroll position to determine active section.
 * On pointer devices, tracks mouse hover instead.
 */
export function useScrollTracking(
  options: UseScrollTrackingOptions,
): UseScrollTrackingReturn {
  const { isTouchDevice, sections } = options;
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [hoveredSectionId, setHoveredSectionId] = useState<string | null>(null);

  const setSectionRef = useCallback(
    (id: string) => (el: HTMLElement | null) => {
      if (el) {
        sectionRefs.current.set(id, el);
      } else {
        sectionRefs.current.delete(id);
      }
    },
    [],
  );

  // Scroll tracking for mobile/touch devices
  useLayoutEffect(() => {
    if (!isTouchDevice) return;

    const container = containerRef.current?.closest(
      '.overflow-y-auto',
    ) as HTMLElement | null;
    if (!container) return;

    const handleScroll = () => {
      const containerRect = container.getBoundingClientRect();
      const viewportCenter = containerRect.top + containerRect.height * 0.3;

      let closestSection: string | null = null;
      let closestDistance = Infinity;

      sectionRefs.current.forEach((el, id) => {
        const rect = el.getBoundingClientRect();
        const sectionTop = rect.top;

        if (sectionTop <= viewportCenter + 100) {
          const distance = Math.abs(viewportCenter - sectionTop);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestSection = id;
          }
        }
      });

      setActiveSectionId(closestSection);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => container.removeEventListener('scroll', handleScroll);
  }, [sections, isTouchDevice]);

  const currentActiveSectionId = isTouchDevice
    ? activeSectionId
    : hoveredSectionId;

  return {
    activeSectionId,
    hoveredSectionId,
    containerRef,
    sectionRefs,
    setSectionRef,
    setHoveredSectionId,
    currentActiveSectionId,
  };
}
